/**
 * Control Inheritance — Deep Merge
 *
 * Rules (all invariants enforced here, nowhere else):
 *   - Plain objects: recursive merge; derived key wins on scalar contention.
 *   - Arrays: id-keyed merge.
 *       • derived item has `id` AND base has an item with the same `id`
 *         → deep-merge those two items (derived wins).
 *       • otherwise → append derived item after base items.
 *   - Everything else (primitives, null, Date, …): derived wins unconditionally.
 *
 * The `id` field is the only array identity key. `key`, `name`, etc. are NOT used —
 * add them here as fallbacks if the schema ever uses them.
 */

function isPlainObject(val: unknown): val is Record<string, unknown> {
  return val !== null && typeof val === 'object' && !Array.isArray(val) &&
    Object.prototype.toString.call(val) === '[object Object]';
}

function getArrayId(item: unknown): string | undefined {
  if (!isPlainObject(item)) return undefined;
  const id = (item as Record<string, unknown>).id;
  return typeof id === 'string' || typeof id === 'number' ? String(id) : undefined;
}

export function mergeArrays(base: unknown[], derived: unknown[]): unknown[] {
  // Index base items by id for O(1) lookup
  const baseById = new Map<string, { index: number; item: unknown }>();
  base.forEach((item, index) => {
    const id = getArrayId(item);
    if (id !== undefined) baseById.set(id, { index, item });
  });

  const result = [...base];

  for (const derivedItem of derived) {
    const id = getArrayId(derivedItem);
    if (id !== undefined && baseById.has(id)) {
      // Merge in place — derived wins
      const { index, item: baseItem } = baseById.get(id)!;
      result[index] = deepMerge(baseItem, derivedItem);
    } else {
      // No matching id → append
      result.push(derivedItem);
    }
  }

  return result;
}

export function deepMerge(base: unknown, derived: unknown): unknown {
  // Arrays — must be checked before isPlainObject
  if (Array.isArray(base) && Array.isArray(derived)) {
    return mergeArrays(base, derived);
  }

  // Plain objects — recurse
  if (isPlainObject(base) && isPlainObject(derived)) {
    const result: Record<string, unknown> = { ...base };
    for (const [key, derivedVal] of Object.entries(derived)) {
      result[key] = deepMerge(base[key], derivedVal);
    }
    return result;
  }

  // Primitive or type mismatch — derived wins (if defined)
  return derived !== undefined ? derived : base;
}
